import { FunctionalComponent, h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import style from "./style.scss";

import ExhibitWork from "components/exhibit/work";
import ExhibitWorkConnector from "components/exhibit/workConnector";
import Page from "components/page";
import { RestartIcon } from "components/icons";
import { withStore } from "components/hocs/withStore";

import { usePrevious } from "hooks/usePrevious";

import { WorkModel } from "models/WorkModel";

import { scrollIntoView } from "utils/dom/scrollIntoView";

const Exhibit: FunctionalComponent = withStore((props) => {
  const { store } = props;
  const elementRef = useRef<HTMLDivElement>(null);
  const placeholderContainerElementRef = useRef<HTMLDivElement>(null);
  const scrollToTimeoutRef = useRef<number | undefined>(undefined);
  const currentPathStartWork = store.exhibitPath[0];
  const currentPathEndWork = store.exhibitPath[store.exhibitPath.length - 1];
  const lastPathStartWork = usePrevious<WorkModel | undefined>(
    store.exhibitPath[0]
  );
  const lastPathEndWork = usePrevious<WorkModel | undefined>(
    store.exhibitPath[store.exhibitPath.length - 1]
  );
  const lastDocumentHeight = useRef(0);
  const lastDocumentScrollPosition = useRef(0);
  const [
    workWithAlternatePathBeingConsidered,
    setWorkWithAlternatePathBeingConsidered,
  ] = useState<WorkModel | undefined>(undefined);
  const shouldRestartButtonShow =
    store.exhibitPath.length > 1 ||
    (store.exhibitPath.length === 1 &&
      store.exhibitPath[0].parentWorks.length > 0);

  useEffect(() => {
    const pathStartChanged = lastPathStartWork !== currentPathStartWork;
    const pathEndChanged = lastPathEndWork !== currentPathEndWork;

    // If the start of the path changed, we immediately adjust the scroll so the
    // page doesn't jump.
    if (pathStartChanged) {
      document.documentElement.scrollTop =
        lastDocumentScrollPosition.current +
        document.documentElement.scrollHeight -
        lastDocumentHeight.current;
    }

    // If either the start or the end changed, we pause and then scroll to the
    // newly added work.
    if (pathStartChanged || pathEndChanged) {
      const workToScrollTo = pathStartChanged
        ? currentPathStartWork
        : currentPathEndWork;
      scrollToTimeoutRef.current = window.setTimeout(() => {
        const workElement = document.getElementById(
          `exhibitWork_${workToScrollTo.id}`
        );
        if (workElement) {
          scrollIntoView(workElement, "center", 1200);
        }
      }, 400);
    }

    return () => {
      window.clearTimeout(scrollToTimeoutRef.current);
    };
  }, [
    currentPathStartWork,
    currentPathEndWork,
    lastDocumentHeight,
    lastDocumentScrollPosition,
    lastPathEndWork,
    lastPathStartWork,
  ]);

  // This removes the placeholder element, if there is one, on every render.
  useEffect(() => {
    requestAnimationFrame(() => {
      if (!placeholderContainerElementRef.current) return;
      placeholderContainerElementRef.current.innerHTML = "";
    });
  });

  return (
    <Page title="Exhibit" description="The TELEPHONE exhibit">
      <div ref={elementRef} class={style.root}>
        {store.exhibitPath.map((work, i) => (
          <div id={`exhibitWork_${work.id}`} key={work.id}>
            {work.parentWorks.length ? (
              <ExhibitWorkConnector
                activeParent={store.exhibitPath[i - 1]}
                isHidden={
                  !(
                    work === workWithAlternatePathBeingConsidered ||
                    store.exhibitPath[i - 1] === undefined
                  )
                }
                relationship="parents"
                work={work}
                onChange={(next) => {
                  handleParentChange(next);
                }}
              />
            ) : null}
            <ExhibitWork
              distance={store.getDistanceAlongExhibitPathFrom(work)}
              work={work}
              pathToWork={store.exhibitPath.slice(0, i + 1)}
              onDirectionChange={() => {
                if (workWithAlternatePathBeingConsidered === work) {
                  setWorkWithAlternatePathBeingConsidered(undefined);
                } else {
                  setWorkWithAlternatePathBeingConsidered(work);
                }
              }}
            />
            <ExhibitWorkConnector
              activeChild={store.exhibitPath[i + 1]}
              isHidden={
                !!(
                  workWithAlternatePathBeingConsidered &&
                  store.exhibitPath[i + 1] ===
                    workWithAlternatePathBeingConsidered
                )
              }
              relationship="children"
              work={work}
              onChange={(next) => {
                handleChildChange(next);
              }}
            />
            {work.childWorks.length === 0 ? (
              <div class={style.endingCard}>
                <p>
                  You have reached the end of this path through{" "}
                  <strong>TELEPHONE</strong>. Click the <strong>Reverse</strong>{" "}
                  button to follow one of the other hundreds of paths back to{" "}
                  the original message. Or click{" "}
                  <button
                    type="button"
                    onClick={() => store.restartExhibitPath()}
                  >
                    Play Again
                  </button>{" "}
                  to explore <strong>TELEPHONE</strong> from the beginning.
                </p>
              </div>
            ) : null}
          </div>
        ))}

        <button
          class={style.pathRestartButton}
          type="button"
          onClick={() => store.restartExhibitPath()}
          aria-label="Restart TELEPHONE path from the beginning"
          data-visible={String(shouldRestartButtonShow)}
        >
          <RestartIcon className={style.pathRestartButtonIcon} />
          <span class={style.pathRestartButtonText}>Restart path</span>
        </button>
      </div>
      <div ref={placeholderContainerElementRef}></div>
    </Page>
  );

  function handleChildChange(next: WorkModel) {
    store.updateOrStartNewPath(next, "forward");
  }

  function handleParentChange(next: WorkModel) {
    if (store.exhibitPath.includes(next)) return;
    lastDocumentHeight.current = document.documentElement.scrollHeight;
    lastDocumentScrollPosition.current = document.documentElement.scrollTop;
    renderPlaceholderElement();
    store.updateOrStartNewPath(next, "backward");
    setWorkWithAlternatePathBeingConsidered(undefined);
  }

  // Renders a clone of the exhibit that is shown while layout below is getting
  // sorted out.
  function renderPlaceholderElement() {
    if (!elementRef.current) return;
    if (!placeholderContainerElementRef.current) return;

    const bounds = elementRef.current.getBoundingClientRect();
    const clone = elementRef.current.cloneNode(true) as HTMLDivElement;
    clone.style.pointerEvents = "none";
    clone.style.position = "fixed";
    clone.style.top = `${bounds.top}px`;
    clone.style.left = `${bounds.left}px`;
    clone.style.height = `${bounds.height}px`;
    clone.style.width = `${bounds.width}px`;
    placeholderContainerElementRef.current.appendChild(clone);
  }
});

export default Exhibit;
